// OPSTASKSALL (2026-10-28, business decision): the COO's "Operational Tasks" card, lifted out of
// Dashboard.js so that every role that handles tasks (HR, compliance, the Country Lead, payroll and
// finance) opens the SAME card: the title with its overdue count, the "Overdue" and "Upcoming" tabs
// with their counts, and the Task, Employee, Due and Due date columns with the "40d overdue" label.
//
// THE CARD DOES NOT DECIDE WHICH TASKS A ROLE SEES. Each shell passes the list it already loads
// (the COO and the Country Lead: GET /dashboard/'s operational_tasks; HR, payroll and finance:
// GET /dashboard/my-tasks; compliance: GET /compliance/report/outstanding-tasks), so every role
// keeps exactly its tasks. The split is the server's is_overdue, the order the server's.
//
// WHAT A CLICK DOES STAYS EACH SHELL'S: `onRowClick(task)` returns the row's handler, or null for
// a row that is not a link; `renderTitle` lets a shell keep its own title control (HR's upload
// link); `renderActions` holds each shell's buttons (Mark complete, Open).
//
// TEXTS: without `t` the card prints the COO's English exactly as before (the COO and the Country
// Lead are unchanged); every other shell passes its translator, so the same words come from
// opsTasks.* in English and French.
import React from 'react';
import { ScrollableTable } from './ui';
// DATEDAY: date-only values shown as their calendar day in every timezone.
import { toDisplayDate } from './dateOnly';

const ENGLISH = {
  'opsTasks.title': 'Operational Tasks',
  'opsTasks.overdue': 'Overdue',
  'opsTasks.upcoming': 'Upcoming',
  'opsTasks.colTask': 'Task',
  'opsTasks.colEmployee': 'Employee',
  'opsTasks.colDue': 'Due',
  'opsTasks.colDueDate': 'Due date',
  'opsTasks.noTasks': 'No tasks.',
  'opsTasks.nothingOverdue': 'Nothing overdue.',
  'opsTasks.nothingUpcoming': 'Nothing upcoming.',
  'opsTasks.noDueDate': 'No due date',
  'opsTasks.daysOverdue': '{{n}}d overdue',
  'opsTasks.upcomingDue': 'Upcoming',
};

export default function OperationalTasksCard({
  tasks, s, isDarkMode, t = null,
  tab, onTabChange,
  error = null,
  titleOf = (task) => task.task_title,
  employeeOf = (task) => task.employee_name,
  renderTitle = null,
  onRowClick = () => null,
  renderActions = () => null,
}) {
  const tr = (key, vars) => {
    if (t) return t(key, vars);
    const text = ENGLISH[key];
    return vars ? text.replace('{{n}}', vars.n) : text;
  };
  const overdueCount = tasks.filter(task => task.is_overdue).length;
  return (
    <div style={s.card}>
      <h3 style={s.cardTitle}>
        {tr('opsTasks.title')}
        {/* TASKBADGEOVERDUE (confirmed decision): OVERDUE only, the same is_overdue filter the
            Overdue tab uses, so the heading badge always equals that tab's count. */}
        {overdueCount > 0 && (
          <span style={s.goldBadge}>{overdueCount}</span>
        )}
      </h3>
      {error && (
        <p style={{ fontSize: '12px', color: '#c0392b', margin: '0 0 10px 0' }}>{error}</p>
      )}
      {tasks.length === 0 ? <p style={s.empty}>{tr('opsTasks.noTasks')}</p> : (() => {
        /* TASKSPLIT: OVERDUE FIRST then UPCOMING, split on the SERVER's is_overdue field. */
        const overdue  = tasks.filter(task => task.is_overdue);
        const upcoming = tasks.filter(task => !task.is_overdue);
        const renderTaskTable = (rows, emptyText) => (
          rows.length === 0 ? (
            <p style={{ ...s.empty, margin: '0 0 4px 0' }}>{emptyText}</p>
          ) : (
        <ScrollableTable><table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>{tr('opsTasks.colTask')}</th>
              <th style={s.th}>{tr('opsTasks.colEmployee')}</th>
              <th style={s.th}>{tr('opsTasks.colDue')}</th>
              <th style={s.th}>{tr('opsTasks.colDueDate')}</th>
              <th style={s.th}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((task, i) => {
              const handler = onRowClick(task);
              return (
              <tr
                key={task.task_id || i}
                style={{ ...s.tr, cursor: handler ? 'pointer' : 'default' }}
                onClick={handler || undefined}
              >
                <td style={s.td}>{renderTitle ? renderTitle(task) : (
                  <span style={{ fontWeight: 500, fontSize: '13px', color: isDarkMode ? '#e8e4d8' : '#1a1a2e' }}>{titleOf(task)}</span>
                )}</td>
                <td style={s.td}>{employeeOf(task)}</td>
                <td style={s.td}>
                  {/* days_overdue = (now - due_date).days: >=0 means the due date has already
                      passed, <0 means it is still upcoming. No precise day count for the upcoming
                      case; the exact date is in the next column. */}
                  {!task.due_date
                    ? <span style={{ fontSize: '12px', color: isDarkMode ? '#9ba8c0' : '#aaa' }}>{tr('opsTasks.noDueDate')}</span>
                    : task.days_overdue >= 0
                      ? <span style={{ fontSize: '12px', color: '#A63A3A', fontWeight: 600 }}>{tr('opsTasks.daysOverdue', { n: task.days_overdue })}</span>
                      : <span style={{ fontSize: '12px', color: isDarkMode ? '#9ba8c0' : '#aaa' }}>{tr('opsTasks.upcomingDue')}</span>}
                </td>
                <td style={s.td}><span style={{ fontSize: '12px', color: isDarkMode ? '#9ba8c0' : '#aaa' }}>{task.due_date ? toDisplayDate(task.due_date).toLocaleDateString() : '-'}</span></td>
                <td style={s.td}>{renderActions(task)}</td>
              </tr>
              );
            })}
          </tbody>
        </table></ScrollableTable>
          )
        );
        // TASKTABS / TASKHEADERTIDY: two pill tabs, the active one filled, each with its count
        // (rendered even at zero) and no enclosing pill around the number.
        const tabBase = {
          backgroundColor: 'transparent',
          border: `0.5px solid ${isDarkMode ? 'rgba(201,168,76,0.15)' : '#E8E4DC'}`,
          borderRadius: '6px', padding: '6px 14px', fontSize: '13px', cursor: 'pointer',
          color: isDarkMode ? '#9ba8c0' : '#888',
          display: 'flex', alignItems: 'center', gap: '5px',
        };
        const tabOn = {
          ...tabBase, backgroundColor: '#1a2744', border: '1px solid transparent',
          color: '#fff', fontWeight: 560,
        };
        const badge = (n, on) => (
          <span style={{
            color: on ? '#fff' : (isDarkMode ? '#9ba8c0' : '#666'),
            fontSize: '11px', fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
          }}>{n}</span>
        );
        const rows = tab === 'overdue' ? overdue : upcoming;
        return (
          <>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              <button
                style={tab === 'overdue' ? tabOn : tabBase}
                aria-pressed={tab === 'overdue'}
                onClick={() => onTabChange('overdue')}
              >
                {tr('opsTasks.overdue')} {badge(overdue.length, tab === 'overdue')}
              </button>
              <button
                style={tab === 'upcoming' ? tabOn : tabBase}
                aria-pressed={tab === 'upcoming'}
                onClick={() => onTabChange('upcoming')}
              >
                {tr('opsTasks.upcoming')} {badge(upcoming.length, tab === 'upcoming')}
              </button>
            </div>
            {renderTaskTable(
              rows,
              tab === 'overdue' ? tr('opsTasks.nothingOverdue') : tr('opsTasks.nothingUpcoming'),
            )}
          </>
        );
      })()}
    </div>
  );
}
